/**
 * Trial Balance report.
 *
 * Sums all posted journal lines per account up to the as-of date and
 * displays them on their natural side (debit-normal vs credit-normal).
 * Verifies that total debits === total credits.
 *
 * Data is derived from real entity data via usePersistentMockState:
 *   - accounts      → chart of accounts for names, types, normal balance
 *   - journalEntries → filter posted entries up to as-of date
 *   - journalLines  → sum debits/credits per account
 *   - openingBalanceConfig → detect OBE account for "Opening" badge
 */

import { useMemo, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Calculator } from "lucide-react";

import { formatMoney, formatMoneySmart } from "@bizop/shared/currency";
import { formatDate } from "@bizop/shared/dates";
import { Badge, KpiCard } from "@bizop/ui-kit";

import { PageHeader } from "../../../../components/PageHeader";
import { usePersistentMockState } from "../../../../lib/usePersistentMockState";
import {
  mockAccounts,
  mockJournalEntries,
  mockJournalLines,
  mockOpeningBalanceConfig,
  type MockAccount,
  type MockJournalEntry,
  type MockJournalLine,
  type MockOpeningBalanceConfig,
  type TrialBalanceRow,
} from "../../../../lib/glMock";
import {
  PeriodSelector,
  defaultPeriodWindow,
  type PeriodWindow,
} from "../../../../components/gl/PeriodSelector";
import { AccountTypeBadge } from "../../../../components/gl/AccountTypeBadge";
import { ExportButton } from "../../../../components/export";
import {
  downloadCsv,
  exportFilename,
  printElement,
  toCsv,
  type ExportColumn,
} from "../../../../lib/export";

export const Route = createFileRoute("/_app/finance/reports/trial-balance")({
  component: TrialBalancePage,
});

/* ── Pure derive function ─────────────────────────────────────── */

function deriveTrialBalance(
  asOfDate: number,
  accounts: MockAccount[],
  entries: MockJournalEntry[],
  lines: MockJournalLine[],
  obeAccountId: string,
): {
  asOfDate: number;
  rows: TrialBalanceRow[];
  totals: { debitsInBaseCents: bigint; creditsInBaseCents: bigint; isBalanced: boolean };
} {
  // Build a set of posted entry IDs up to asOfDate
  const postedEntryIds = new Set<string>();
  for (const e of entries) {
    if (e.status === "posted" && e.entryDate <= asOfDate) {
      postedEntryIds.add(e._id);
    }
  }

  // Accumulate net (debit - credit) per account from posted lines
  const netByAccount = new Map<string, bigint>();
  for (const l of lines) {
    if (!postedEntryIds.has(l.journalEntryId)) continue;
    if (l.entryDate > asOfDate) continue;
    const prev = netByAccount.get(l.accountId) ?? 0n;
    netByAccount.set(l.accountId, prev + l.debitInBaseCents - l.creditInBaseCents);
  }

  const rows: TrialBalanceRow[] = [];
  let totalDebits = 0n;
  let totalCredits = 0n;

  for (const acc of accounts) {
    // Skip pure-parent placeholder accounts (code 1500 pattern)
    if (acc._id === "acc_1500") continue;

    const net = netByAccount.get(acc._id) ?? 0n;
    const debitNormal = acc.normalBalance === "debit";

    // Convert net to natural-side balance
    const balance = debitNormal ? net : -net;

    const debitColumn =
      debitNormal && balance >= 0n
        ? balance
        : !debitNormal && balance < 0n
          ? -balance
          : 0n;
    const creditColumn =
      !debitNormal && balance >= 0n
        ? balance
        : debitNormal && balance < 0n
          ? -balance
          : 0n;

    rows.push({
      accountId: acc._id,
      code: acc.code,
      name: acc.name,
      accountType: acc.accountType,
      debitsInBaseCents: debitColumn,
      creditsInBaseCents: creditColumn,
      naturalSide: acc.normalBalance,
      isOpeningBalance: acc._id === obeAccountId ? true : undefined,
    });
    totalDebits += debitColumn;
    totalCredits += creditColumn;
  }

  rows.sort((a, b) => a.code.localeCompare(b.code));

  return {
    asOfDate,
    rows,
    totals: {
      debitsInBaseCents: totalDebits,
      creditsInBaseCents: totalCredits,
      isBalanced: totalDebits === totalCredits,
    },
  };
}

/* ── Component ────────────────────────────────────────────────── */

function TrialBalancePage() {
  const [period, setPeriod] = useState<PeriodWindow>(defaultPeriodWindow());

  const [accounts] = usePersistentMockState<MockAccount[]>("accounts", mockAccounts);
  const [entries] = usePersistentMockState<MockJournalEntry[]>("journalEntries", mockJournalEntries);
  const [lines] = usePersistentMockState<MockJournalLine[]>("journalLines", mockJournalLines);
  const [obConfig] = usePersistentMockState<MockOpeningBalanceConfig>(
    "openingBalanceConfig",
    mockOpeningBalanceConfig,
  );

  const report = useMemo(
    () => deriveTrialBalance(period.toDate, accounts, entries, lines, obConfig.obeAccountId),
    [period.toDate, accounts, entries, lines, obConfig.obeAccountId],
  );
  const printRef = useRef<HTMLDivElement>(null);

  function handleCsv(extension: "csv" | "xls") {
    type Row = TrialBalanceRow & { balanceCents: bigint };
    const rows: Row[] = report.rows.map((r) => ({
      ...r,
      balanceCents: r.debitsInBaseCents - r.creditsInBaseCents,
    }));
    const columns: ExportColumn<Row>[] = [
      { key: "code", label: "Account code" },
      { key: "name", label: "Name" },
      { key: "accountType", label: "Type" },
      { key: "debitsInBaseCents", label: "Debit" },
      { key: "creditsInBaseCents", label: "Credit" },
      { key: "balanceCents", label: "Balance" },
      { key: "currency", label: "Currency", value: () => "BDT" },
    ];
    const csv = toCsv(rows, columns);
    downloadCsv(exportFilename("trial-balance", extension), csv);
  }

  function handlePrint() {
    printElement(printRef.current, {
      title: "Trial Balance",
      subtitle: `As of ${formatDate(period.toDate, { style: "medium" })}`,
    });
  }

  // Opening-balance hint
  const openingPosted =
    obConfig.status === "posted" || obConfig.status === "closed";
  const openingHint = openingPosted
    ? `Includes opening balance entry posted on ${formatDate(obConfig.asOfDate, { style: "medium" })}.`
    : null;

  return (
    <div className="flex flex-col gap-6" ref={printRef}>
      <PageHeader
        title="Trial Balance"
        description={`Balanced ledger as of ${formatDate(period.toDate, { style: "medium" })}.${openingHint ? ` ${openingHint}` : ""}`}
        actions={
          <ExportButton
            onCsv={() => handleCsv("csv")}
            onExcel={() => handleCsv("xls")}
            onPrint={handlePrint}
          />
        }
      />

      <PeriodSelector value={period} onChange={setPeriod} />

      <section
        className="grid grid-cols-1 sm:grid-cols-3 gap-4 print-kpis"
        aria-label="Trial balance KPIs"
      >
        <KpiCard
          caption="Total debits"
          value={formatMoneySmart(report.totals.debitsInBaseCents, "BDT", {
            locale: "en-BD",
          })}
          hint={`${report.rows.length} accounts`}
        />
        <KpiCard
          caption="Total credits"
          value={formatMoneySmart(report.totals.creditsInBaseCents, "BDT", {
            locale: "en-BD",
          })}
          hint="Should equal debits"
        />
        <KpiCard
          caption="Balanced?"
          value={report.totals.isBalanced ? "Yes" : "No"}
          hint={
            report.totals.isBalanced
              ? "Debits = Credits"
              : `Δ ${formatMoneySmart(
                  report.totals.debitsInBaseCents -
                    report.totals.creditsInBaseCents,
                  "BDT",
                  { locale: "en-BD" },
                )}`
          }
        />
      </section>

      <div className="rounded-md border border-(--border-subtle) bg-(--bg-canvas) overflow-hidden">
        <table className="w-full text-[13px]">
          <thead className="bg-(--bg-surface) text-(--text-tertiary) text-[11px] uppercase tracking-wider">
            <tr>
              <th className="px-3 py-2 text-left">Code</th>
              <th className="px-3 py-2 text-left">Name</th>
              <th className="px-3 py-2 text-left">Type</th>
              <th className="px-3 py-2 text-right" data-numeric="true">Debit</th>
              <th className="px-3 py-2 text-right" data-numeric="true">Credit</th>
            </tr>
          </thead>
          <tbody>
            {report.rows.map((row) => (
              <TbRow key={row.accountId} row={row} />
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-(--border-subtle) bg-(--bg-surface) font-semibold">
              <td className="px-3 py-2" />
              <td className="px-3 py-2">TOTAL</td>
              <td className="px-3 py-2" />
              <td className="px-3 py-2 text-right font-mono tabular-nums" data-numeric="true">
                {formatMoney(report.totals.debitsInBaseCents, "BDT", {
                  locale: "en-BD",
                })}
              </td>
              <td
                className={
                  report.totals.isBalanced
                    ? "px-3 py-2 text-right font-mono tabular-nums"
                    : "px-3 py-2 text-right font-mono tabular-nums text-(--danger)"
                }
                data-numeric="true"
              >
                {formatMoney(report.totals.creditsInBaseCents, "BDT", {
                  locale: "en-BD",
                })}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {!report.totals.isBalanced ? (
        <div className="rounded-md border border-(--danger) bg-(--bg-canvas) p-3 text-[13px] text-(--danger)">
          <Calculator className="inline-block mr-1 h-4 w-4" /> Trial balance does
          NOT balance. Investigate journal entries before closing the period.
        </div>
      ) : null}
    </div>
  );
}

function TbRow({ row }: { row: TrialBalanceRow }) {
  return (
    <tr className="border-t border-(--border-subtle) hover:bg-(--bg-hover)">
      <td className="px-3 py-2 font-mono text-[12px] text-(--text-primary)">
        {row.code}
      </td>
      <td className="px-3 py-2 text-(--text-primary)">
        <span className="inline-flex items-center gap-2">
          {row.name}
          {row.isOpeningBalance ? (
            <Badge
              variant="info"
              size="sm"
              title="Opening Balance Equity — offset account for one-shot opening balance entries."
            >
              Opening
            </Badge>
          ) : null}
        </span>
      </td>
      <td className="px-3 py-2">
        <AccountTypeBadge type={row.accountType} />
      </td>
      <td className="px-3 py-2 text-right font-mono tabular-nums text-(--text-primary)" data-numeric="true">
        {row.debitsInBaseCents > 0n
          ? formatMoney(row.debitsInBaseCents, "BDT", { locale: "en-BD" })
          : "—"}
      </td>
      <td className="px-3 py-2 text-right font-mono tabular-nums text-(--text-primary)" data-numeric="true">
        {row.creditsInBaseCents > 0n
          ? formatMoney(row.creditsInBaseCents, "BDT", { locale: "en-BD" })
          : "—"}
      </td>
    </tr>
  );
}
